/* ═══════════════════════════════════════════════════════════════
   PiARIS — Issue Screenshots widget

   Displays thumbnails for each screenshot attached to an issue, plus an
   "Add screenshot" control for manual uploads by auditors. Clicking a
   thumbnail opens the Lightbox component for full-size viewing with
   prev/next navigation.

   Props:
     sessionId:  "sess-*" (Watchtower) or "psess-*" (PA11YLab)
     issueId:    stable id of the issue
     pageIndex:  number for Watchtower, null for psess
     screenshots: current array from issue.screenshots (server source of truth)
     onMutate:   called with updated screenshots array after upload/delete,
                 so the parent can update its local state

   Contract:
     Parent is responsible for passing the screenshots from the latest
     server state. This component POSTs/DELETEs and reports back what
     the server returned.
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P } from "../constants";
import { Lightbox } from "./Lightbox";

var E = React.createElement;

/* Read a File object as a base64 data URL. Browser-only. */
function fileToDataUrl(file) {
  return new Promise(function (resolve, reject) {
    var fr = new FileReader();
    fr.onload = function () { resolve(fr.result); };
    fr.onerror = function () { reject(fr.error); };
    fr.readAsDataURL(file);
  });
}

function IssueScreenshots(props) {
  var readOnly = props.readOnly === true;

  /* Local state seeded from props. We sync when prop.screenshots changes
     (parent pulled fresh data from server) but also maintain our own
     optimistic updates on upload/delete so the UI reflects the change
     without requiring the parent to re-render. */
  var _shots = React.useState(Array.isArray(props.screenshots) ? props.screenshots : []);
  var shots = _shots[0];
  var setShots = _shots[1];

  React.useEffect(function () {
    setShots(Array.isArray(props.screenshots) ? props.screenshots : []);
  }, [props.screenshots]);

  /* Lazy auto-crop: when the widget mounts, if the issue has a bbox but
     no screenshots at all, ask the server to derive one from the page's
     rawScreenshotUrl. This is how full-page extension captures become
     per-issue evidence in the audit report without any eager processing.

     Guards:
       - runs once per issueId (tracks via ref)
       - requires sessionId + bbox present
       - no-op when readOnly (exported reports) or shots already populated
       - silent-fail: any error (including 409 "no rawScreenshotUrl") just
         leaves the issue without a screenshot. Never blocks the UI. */
  var triedDerive = React.useRef(false);
  React.useEffect(function () {
    if (readOnly) return;
    if (triedDerive.current) return;
    if (shots.length > 0) return;
    if (!props.sessionId || !props.issueId || !props.bbox) return;
    var bx = props.bbox;
    if (typeof bx.x !== "number" || typeof bx.y !== "number" ||
        typeof bx.width !== "number" || typeof bx.height !== "number") return;
    triedDerive.current = true;
    fetch("/api/issue-screenshot/derive", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: props.sessionId,
        pageIndex: props.pageIndex === undefined ? null : props.pageIndex,
        issueId:   props.issueId,
        bbox:      bx
      })
    }).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    }).then(function (entry) {
      if (!entry || !entry.id) return;
      setShots(function (current) { return current.concat([entry]); });
      if (props.onMutate) props.onMutate([].concat(entry));
    }).catch(function () { /* silent — evidence is best-effort */ });
  }, [props.sessionId, props.issueId, props.pageIndex, props.bbox, readOnly, shots.length]);

  var ref = React.useState({ lightboxIdx: -1, uploading: false, error: null });
  var state = ref[0];
  var setState = ref[1];

  function openLightbox(i) { setState(function (s) { return Object.assign({}, s, { lightboxIdx: i }); }); }
  function closeLightbox()  { setState(function (s) { return Object.assign({}, s, { lightboxIdx: -1 }); }); }
  function setLightboxIdx(i) { setState(function (s) { return Object.assign({}, s, { lightboxIdx: i }); }); }

  async function handleUpload(file) {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setState(function (s) { return Object.assign({}, s, { error: "Please choose an image file" }); });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setState(function (s) { return Object.assign({}, s, { error: "Image too large (max 8 MB)" }); });
      return;
    }
    setState(function (s) { return Object.assign({}, s, { uploading: true, error: null }); });

    try {
      var dataUrl = await fileToDataUrl(file);
      var resp = await fetch("/api/issue-screenshot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: props.sessionId,
          issueId: props.issueId,
          pageIndex: props.pageIndex === undefined ? null : props.pageIndex,
          base64Data: dataUrl,
          source: "manual"
        })
      });
      if (!resp.ok) throw new Error("Upload failed (" + resp.status + ")");
      var created = await resp.json();
      var next = shots.concat([created]);
      setShots(next);
      setState(function (s) { return Object.assign({}, s, { uploading: false }); });
      props.onMutate && props.onMutate(next);
    } catch (e) {
      setState(function (s) { return Object.assign({}, s, { uploading: false, error: e.message }); });
    }
  }

  async function handleDelete(screenshotId) {
    if (!window.confirm("Remove this screenshot?")) return;
    try {
      var qs = props.pageIndex === undefined || props.pageIndex === null ? "" :
               ("?pageIndex=" + encodeURIComponent(props.pageIndex));
      var url = "/api/issue-screenshot/" + encodeURIComponent(props.sessionId) +
                "/" + encodeURIComponent(props.issueId) +
                "/" + encodeURIComponent(screenshotId) + qs;
      var resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) throw new Error("Delete failed (" + resp.status + ")");
      var next = shots.filter(function (s) { return s.id !== screenshotId; });
      setShots(next);
      props.onMutate && props.onMutate(next);
    } catch (e) {
      setState(function (s) { return Object.assign({}, s, { error: e.message }); });
    }
  }

  /* Input ref for programmatically opening the file chooser */
  var inputRef = React.useRef(null);

  return E("div", { style: { marginTop: 10 } },
    E("div", {
      style: {
        fontSize: 10, fontWeight: 800, color: P.slate, textTransform: "uppercase",
        letterSpacing: ".04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 8
      }
    },
      E("span", null, shots.length > 0 ? "Screenshots (" + shots.length + ")" : "Screenshots"),
      !readOnly ? E("button", {
        onClick: function () { inputRef.current && inputRef.current.click(); },
        disabled: state.uploading,
        style: {
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
          padding: "3px 8px", borderRadius: 4, border: "1px solid " + P.bdr,
          background: P.card, color: P.slateM, cursor: state.uploading ? "wait" : "pointer"
        }
      }, state.uploading ? "Uploading\u2026" : "+ Add") : null,
      !readOnly ? E("input", {
        ref: inputRef,
        type: "file",
        accept: "image/*",
        style: { display: "none" },
        onChange: function (e) {
          var f = e.target.files && e.target.files[0];
          handleUpload(f);
          e.target.value = "";
        }
      }) : null
    ),

    state.error ? E("div", {
      role: "alert",
      style: { fontSize: 11, color: P.sCrit, marginBottom: 6 }
    }, state.error) : null,

    shots.length === 0 ? E("div", {
      style: {
        fontSize: 11, color: P.slateL, fontStyle: "italic", padding: "6px 0"
      }
    }, readOnly ? "No screenshots attached." : "No screenshots yet. Click + Add to upload evidence.") :

    /* Thumbnail grid */
    E("div", {
      style: { display: "flex", flexWrap: "wrap", gap: 8 }
    },
      shots.map(function (s, i) {
        return E("div", {
          key: s.id,
          style: { position: "relative", width: 96, height: 72 }
        },
          E("button", {
            onClick: function () { openLightbox(i); },
            "aria-label": "Open screenshot " + (i + 1) + (s.caption ? ": " + s.caption : ""),
            style: {
              width: "100%", height: "100%", padding: 0,
              border: "1px solid " + P.bdr, borderRadius: 6,
              background: "#f7f8fa url(" + s.url + ") center/cover no-repeat",
              cursor: "pointer", position: "relative"
            }
          },
            s.source === "extension" ? E("span", {
              title: "Auto-captured by extension",
              style: {
                position: "absolute", top: 3, left: 3,
                background: "rgba(0,0,0,0.65)", color: "#fff",
                fontSize: 8, padding: "1px 4px", borderRadius: 3,
                fontWeight: 700, letterSpacing: ".04em"
              }
            }, "AUTO") : null
          ),
          !readOnly ? E("button", {
            onClick: function () { handleDelete(s.id); },
            "aria-label": "Remove screenshot " + (i + 1),
            title: "Remove",
            style: {
              position: "absolute", top: -6, right: -6,
              width: 20, height: 20, borderRadius: "50%",
              background: "#fff", border: "1px solid " + P.bdr,
              fontSize: 11, lineHeight: 1, cursor: "pointer",
              color: P.sCrit, fontWeight: 700
            }
          }, "\u00D7") : null
        );
      })
    ),

    /* Lightbox overlay */
    state.lightboxIdx >= 0 ? E(Lightbox, {
      images: shots,
      index: state.lightboxIdx,
      onClose: closeLightbox,
      onIndex: setLightboxIdx
    }) : null
  );
}

export { IssueScreenshots };
